import { useState, useCallback, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import toast from "components/partials/toast/toast";
import { SelectedTouchpointsCampaignModal } from "containers/admin/clients/campaign/campaign-creative-page/components/selected-touchpoints-campaign-modal/selected-touchpoints-campaign-modal";
import { useClientCampaignsInModal } from "hooks/use-client-campaigns";
import { folderKeys, touchpointKeys } from "hooks/api/query-keys";
import { usePartiallyUpdateTouchpoint } from "hooks/api/use-touchpoints";
import { getErrorMessage } from "utilities";
import { settleWithConcurrency } from "utilities/settle-with-concurrency";
import type { TouchpointAttributes } from "hooks/api/use-touchpoints";
import type { OptionType } from "ui/select";
import type { PaginatedRequestOptions } from "types/pagination";

const CAMPAIGN_REQUEST_OPTIONS: PaginatedRequestOptions = { unpaged: true };

/**
 * There is no bulk endpoint, so each creative is a separate PATCH. Kept below the browser's
 * 6-connections-per-origin HTTP/1.1 cap so the rest of the page can still load while a large
 * selection is being moved.
 */
const MOVE_CONCURRENCY = 4;

type UseMoveTouchpointsToCampaignModalProps = {
  onSuccess?: () => void;
};

export const useMoveTouchpointsToCampaignModal = ({
  onSuccess,
}: UseMoveTouchpointsToCampaignModalProps = {}) => {
  const queryClient = useQueryClient();
  const { mutateAsync: patchTouchpoint } = usePartiallyUpdateTouchpoint();
  const [itemsToMove, setItemsToMove] = useState<TouchpointAttributes[]>([]);
  const [targetCampaignId, setTargetCampaignId] = useState("");
  const [targetFolderId, setTargetFolderId] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isOpen = itemsToMove.length > 0;
  const sourceCampaignId = itemsToMove[0]?.campaignId;
  const clientCampaigns = useClientCampaignsInModal(isOpen, CAMPAIGN_REQUEST_OPTIONS);

  const campaignOptions = useMemo<OptionType[]>(
    () =>
      clientCampaigns
        .filter((campaign) => campaign.id !== sourceCampaignId)
        .map((campaign) => ({ label: campaign.name, value: campaign.id })),
    [clientCampaigns, sourceCampaignId],
  );

  const openMoveToCampaignModal = useCallback(
    (items: TouchpointAttributes | TouchpointAttributes[]) => {
      setItemsToMove(Array.isArray(items) ? items : [items]);
      setTargetCampaignId("");
      setTargetFolderId("");
    },
    [],
  );

  const closeMoveToCampaignModal = useCallback(() => {
    if (isSubmitting) return;
    setItemsToMove([]);
    setTargetCampaignId("");
    setTargetFolderId("");
  }, [isSubmitting]);

  const handleCampaignChange = useCallback((campaignId: string) => {
    setTargetCampaignId(campaignId);
    setTargetFolderId("");
  }, []);

  const handleMoveToCampaign = useCallback(async () => {
    if (itemsToMove.length === 0 || !targetCampaignId) return;

    setIsSubmitting(true);
    try {
      const results = await settleWithConcurrency(itemsToMove, MOVE_CONCURRENCY, (item) =>
        patchTouchpoint({
          id: item.id,
          data: { campaignId: targetCampaignId, folderId: targetFolderId || undefined },
          meta: { skipInvalidation: true },
        }),
      );

      const movedIds = new Set(
        itemsToMove.filter((_, index) => results[index].status === "fulfilled").map((item) => item.id),
      );
      const failed = results.filter(
        (result): result is PromiseRejectedResult => result.status === "rejected",
      );

      if (movedIds.size) {
        queryClient.invalidateQueries({ queryKey: touchpointKeys.all });
        queryClient.invalidateQueries({ queryKey: folderKeys.all });
        const targetCampaignName =
          clientCampaigns.find((campaign) => campaign.id === targetCampaignId)?.name ?? "campaign";
        const creativesLabel = movedIds.size > 1 ? "Creatives" : "Creative";
        toast.success({ data: { title: `${creativesLabel} moved to ‘${targetCampaignName}’` } });
      }

      if (failed.length) {
        toast.error({
          data: {
            title: `Failed to move ${failed.length} of ${results.length} creatives`,
            message: getErrorMessage(failed[0].reason),
          },
        });
        // Moved creatives drop out of the table selection on refetch; the failed ones stay
        // selected so they still match what the modal lists for a retry.
        setItemsToMove((prev) => prev.filter((item) => !movedIds.has(item.id)));
        return;
      }

      onSuccess?.();
      setItemsToMove([]);
      setTargetCampaignId("");
      setTargetFolderId("");
    } finally {
      setIsSubmitting(false);
    }
  }, [
    itemsToMove,
    targetCampaignId,
    targetFolderId,
    clientCampaigns,
    patchTouchpoint,
    queryClient,
    onSuccess,
  ]);

  const moveToCampaignModalMarkup = isOpen ? (
    <SelectedTouchpointsCampaignModal
      onCloseCallback={closeMoveToCampaignModal}
      onConfirmCallback={handleMoveToCampaign}
      selectedList={itemsToMove}
      campaignOptions={campaignOptions}
      campaignId={targetCampaignId}
      setCampaignId={handleCampaignChange}
      folderId={targetFolderId}
      setFolderId={setTargetFolderId}
      clientId={itemsToMove[0].clientId}
      isSubmitting={isSubmitting}
    />
  ) : null;

  return {
    openMoveToCampaignModal,
    moveToCampaignModalMarkup,
  };
};
